import { QuestionMarkIcon, ShieldCheckIcon } from '@/assets/svg'
import { CheckboxSelect } from '@/components/fragments'
import WrapperCard from '@/components/fragments/wrapper-card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useState, useEffect } from 'react'
import { useUpdateSettingsMutation } from '@/redux/services/settings'
import { toast } from 'sonner'
import { getChangedFields } from './notifications'

const getSystemSettingsFromAPI = (data: any) => {
  if (!data) {
    return {}
  }

  const settingsMap: Record<string, any> = {}

  if (data.contact_information) {
    data.contact_information.forEach((item: any) => {
      settingsMap[item.key] = item.value
    })
  }

  if (data.platform_controls) {
    data.platform_controls.forEach((item: any) => {
      settingsMap[item.key] = item.value
    })
  }

  return settingsMap
}

// export const getChangedFields = (settings: any, originalSettings: any) => {
//   return Object.keys(settings)
//     .filter(
//       (key) =>
//         settings[key as keyof typeof settings] !==
//         originalSettings[key as keyof typeof originalSettings]
//     )
//     .map((key) => ({
//       key,
//       value: settings[key as keyof typeof settings],
//     }))
// }

interface SystemSettingsProps {
  data: any
  isLoading?: boolean
  isLoadingSettings?: boolean
}

const SystemSettings = ({ data, isLoadingSettings }: SystemSettingsProps) => {
  const [settings, setSettings] = useState(getSystemSettingsFromAPI(data))
  const [originalSettings, setOriginalSettings] = useState(
    getSystemSettingsFromAPI(data)
  )
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation()

  useEffect(() => {
    if (data) {
      const newSettings = getSystemSettingsFromAPI(data)
      setSettings(newSettings)
      setOriginalSettings(newSettings)
    }
  }, [data])

  const handleSettingChange = (key: string, value: boolean | string) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleSaveChanges = async () => {
    const changedFields = getChangedFields(settings, originalSettings)

    if (changedFields.length === 0) {
      toast.info('No changes to save')
      return
    }

    updateSettings({ settings: changedFields })
      .unwrap()
      .then((response: any) => {
        if (response.success) {
          toast.success(response.message)
        } else {
          toast.error(response.message)
        }
      })
      .catch((error: any) => {
        toast.error(error.data.message)
      })
  }

  const handleReset = () => {
    const resetSettings = getSystemSettingsFromAPI(data)
    setSettings(resetSettings)
    setOriginalSettings(resetSettings)
  }

  return (
    <div className='space-y-6 mt-4 '>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <WrapperCard
          title='Platform Controls'
          icon={ShieldCheckIcon}
          wrapperClassName='h-full flex flex-col justify-start'
          headerClassName='flex flex-row-reverse justify-end items-center gap-2 text-[#424242]'
          loading={isLoadingSettings}
        >
          <div className='space-y-4 mt-4 bg-[#f5f5f5] p-2 md:p-4 rounded-[12px]'>
            {data?.platform_controls?.map((item: any) => (
              <CheckboxSelect
                key={item.key}
                id={item.key}
                label={item.label}
                description={item.description}
                checked={settings[item.key] || false}
                onChange={(checked) => handleSettingChange(item.key, checked)}
                containerClass='rounded-[8px] bg-white p-2.5'
              />
            ))}
          </div>
        </WrapperCard>

        <WrapperCard
          title='Contact Information'
          icon={QuestionMarkIcon}
          wrapperClassName='h-full flex flex-col justify-start'
          headerClassName='flex flex-row-reverse justify-end items-center gap-2 text-[#424242]'
          loading={isLoadingSettings}
        >
          <div className='space-y-4 mt-4 bg-[#f5f5f5] p-2 md:p-4 rounded-[12px]'>
            {data?.contact_information?.map((item: any, index: number) => (
              <div
                key={item.key}
                className={`rounded-[8px] p-2.5 ${index === 0 ? 'py-0' : 'pb-0'}`}
              >
                <Input
                  id={item.key}
                  name={item.key}
                  label={item.label}
                  type={item.type}
                  value={settings[item.key] || ''}
                  onChange={(e) =>
                    handleSettingChange(item.key, e.target.value)
                  }
                  containerClass='mb-0'
                  labelClass='text-[14px] SFProRegular'
                  inputClass='!bg-white !border-[#E5E9EB] rounded-[1rem]'
                />
              </div>
            ))}
          </div>
        </WrapperCard>
      </div>

      <div className='flex justify-start space-x-4 pt-4'>
        <Button
          onClick={handleSaveChanges}
          loading={isLoading}
          disabled={isLoading}
          loadingText='Saving...'
          className='w-auto px-6'
        >
          Save Changes
        </Button>
        <Button
          variant='outline'
          onClick={handleReset}
          className='w-auto px-6'
          disabled={isLoading}
        >
          Reset
        </Button>
      </div>
    </div>
  )
}

export default SystemSettings
